// Shared date/time formatting utilities (S15).
//
// All displayed date/time strings should go through these helpers
// so they respect the user's locale settings via Intl.DateTimeFormat.
// This replaces the hardcoded English month-name arrays that were
// scattered across KundliIntakeReview and CompatibilityReportShell.

/**
 * Format a birth date string (YYYY-MM-DD) for display.
 * Returns a locale-aware short date like "15 May 1990" (en-US) or
 * "15 mai 1990" (fr-FR). Falls back to the raw value if the input
 * is empty or malformed.
 */
export function formatBirthDate(value: string): string {
  return formatDateOnly(value);
}

/**
 * Format a birth date string (YYYY-MM-DD) with both the machine
 * form and the human form, e.g. "1990-05-15 (15 May 1990)".
 * This preserves the dual-form display from the original
 * KundliIntakeReview while making the human form locale-aware.
 */
export function formatBirthDateDual(value: string): string {
  if (!value) return value;
  const formatted = formatBirthDate(value);
  if (formatted === value) return value;
  return `${value} (${formatted})`;
}

/**
 * Format a birth time string (HH:MM) for display in a locale-aware
 * way. Returns something like "10:30 AM" (en-US) or "10:30" (fr-FR).
 * Falls back to the raw value if the input is malformed.
 */
export function formatBirthTime(value: string): string {
  if (!value) return value;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return value;
  const [, h, m] = match;
  const hours = Number(h);
  const minutes = Number(m);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format an ISO timestamp (or Date) for display in reading history
 * and report metadata. Returns a locale-aware date+time string.
 */
export function formatTimestamp(iso: string | Date): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return typeof iso === 'string' ? iso : '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Format an ISO timestamp (or Date, or any Date-parseable string) as
 * a locale-aware date-only string, e.g. "15 May 1990". Used for
 * report date ranges (dasha periods, expiry dates) where the time
 * component is not relevant. Falls back to the raw value on parse
 * failure.
 */
export function formatDateOnly(iso: string | Date, locales?: string | string[]): string {
  if (!iso) return '';
  // A birth date is a calendar value, not an instant. Parsing YYYY-MM-DD
  // with the browser's local timezone can move it to the previous day in
  // western timezones, so keep date-only values in UTC for formatting.
  const isoDateOnly = typeof iso === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const slashDateOnly = typeof iso === 'string' && /^(\d{2})[/-](\d{2})[/-](\d{4})$/.exec(iso);
  const date = isoDateOnly
    ? new Date(Date.UTC(Number(isoDateOnly[1]), Number(isoDateOnly[2]) - 1, Number(isoDateOnly[3])))
    : slashDateOnly
      ? new Date(Date.UTC(Number(slashDateOnly[3]), Number(slashDateOnly[2]) - 1, Number(slashDateOnly[1])))
      : new Date(iso);
  if (Number.isNaN(date.getTime())) return typeof iso === 'string' ? iso : '';
  return new Intl.DateTimeFormat(locales, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...((isoDateOnly || slashDateOnly) ? { timeZone: 'UTC' } : {}),
  }).format(date);
}

/**
 * Format a birth date string (YYYY-MM-DD) with a long month name,
 * e.g. "15 March 1990". This is the long-form variant of
 * formatBirthDate used by report hero subtitles where the longer
 * month reads better. Falls back to the raw value on parse failure.
 */
export function formatBirthDateLong(value: string): string {
  if (!value) return value;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, y, m, d] = match;
  const year = Number(y);
  const monthIdx = Number(m) - 1;
  const day = Number(d);
  if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || !Number.isFinite(day)) {
    return value;
  }
  const date = new Date(Date.UTC(year, monthIdx, day));
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Format an ISO timestamp (or Date, or any Date-parseable string) as
 * a locale-aware time-only string, e.g. "10:30 AM". Used for chat
 * message timestamps and other time-only displays. Falls back to
 * the raw value on parse failure.
 */
export function formatTimeOnly(iso: string | Date): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return typeof iso === 'string' ? iso : '';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}
